<h2 mat-dialog-title>{{ data.title }}</h2>

<mat-dialog-content #dialogContent>
    <!-- Tab group: hidden when cropping to give cropper full space -->
    <mat-tab-group *ngIf="state() !== 'cropping'" [selectedIndex]="selectedTabIndex()" (selectedIndexChange)="onTabChange($event)" animationDuration="0ms">
        <!-- TAB 1: Generate -->
        <mat-tab [label]="'aiImageTabGenerate' | translate">
            <ng-template matTabContent>
                <!-- Prompt input -->
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>{{ 'aiImagePromptLabel' | translate }}</mat-label>
                    <textarea matInput [(ngModel)]="prompt" [placeholder]="'aiImagePromptPlaceholder' | translate" rows="3" maxlength="500"></textarea>
                    <mat-hint align="end">{{ prompt.length }} / 500</mat-hint>
                </mat-form-field>

                <!-- Hourly usage counter -->
                <div class="hourly-usage" *ngIf="hourlyLimit() !== null && !hourlyLimitReached()">
                    <span>{{ 'aiImageHourlyUsage' | translate : { used: hourlyUsed(), limit: hourlyLimit(), remaining: hourlyRemaining() } }}</span>
                </div>

                <!-- Hourly limit reached banner -->
                <div class="error-message" *ngIf="hourlyLimitReached()">
                    <mat-icon color="warn">error</mat-icon>
                    <span>{{ 'aiImageHourlyLimitReached' | translate : { used: hourlyUsed(), limit: hourlyLimit() } }}</span>
                </div>

                <!-- Error message -->
                <div class="error-message" *ngIf="errorMessage">
                    <mat-icon color="warn">error</mat-icon>
                    <span>{{ errorMessage | translate }}</span>
                </div>

                <!-- Image grid (2x2): shows finished images plus a busy placeholder per in-flight request -->
                <div class="image-grid" *ngIf="state() === 'results' || state() === 'selected'">
                    <p class="from-cache-hint" *ngIf="fromCache">{{ 'aiImagePreviouslyGenerated' | translate }}</p>
                    <p class="select-hint" *ngIf="generating()">{{ 'aiImageGeneratingMessage' | translate }}</p>
                    <p class="select-hint" *ngIf="!generating()">{{ 'aiImageSelectImageHint' | translate }}</p>
                    <div class="grid">
                        <div class="grid-item" *ngFor="let img of generatedImages" [class.selected]="selectedImage === img" (click)="selectImage(img)">
                            <img [src]="img.image?.url" [alt]="'Generated image ' + img.image?.index" />
                        </div>
                        <!-- Busy slots for requests still generating/retrying -->
                        <div class="grid-item loading" *ngFor="let slot of pendingSlots()" aria-busy="true">
                            <div class="spinner"></div>
                        </div>
                    </div>
                </div>
            </ng-template>
        </mat-tab>

        <!-- TAB 2: Gallery -->
        <mat-tab [label]="'aiImageTabGallery' | translate">
            <ng-template matTabContent>
                <!-- Loading spinner -->
                <div class="generating-container" *ngIf="galleryService.loading$ | async">
                    <div class="spinner"></div>
                </div>

                <!-- Error state -->
                <div class="error-message" *ngIf="galleryService.error$ | async as err">
                    <mat-icon color="warn">error</mat-icon>
                    <span>{{ err | translate }}</span>
                </div>

                <!-- Empty state -->
                <div class="gallery-empty" *ngIf="galleryService.isEmpty$ | async">
                    <mat-icon>collections</mat-icon>
                    <span>{{ 'aiImageGalleryEmpty' | translate }}</span>
                </div>

                <!-- Image grid -->
                <div class="gallery-grid" *ngIf="galleryService.images$ | async as images">
                    <div class="grid" *ngIf="images.length > 0">
                        <div class="grid-item" *ngFor="let img of images" [class.selected]="selectedGalleryImage() === img" [matTooltip]="img.prompt ?? ''" (click)="selectGalleryImage(img)">
                            <img [src]="img.imageUrl" [alt]="'Gallery image'" loading="lazy" />
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div class="gallery-pagination" *ngIf="images.length > 0">
                        <por-base-button
                            type="button"
                            class="transparent"
                            [disabled]="(galleryService.hasPrev$ | async) === false"
                            (onclick)="galleryService.prevPage()"
                            [featureName]="'gallery-prev-page'">
                            <mat-icon>chevron_left</mat-icon>
                        </por-base-button>

                        <span>{{ galleryService.page$ | async }} / {{ galleryService.totalPages$ | async }}</span>

                        <por-base-button
                            type="button"
                            class="transparent"
                            [disabled]="(galleryService.hasNext$ | async) === false"
                            (onclick)="galleryService.nextPage()"
                            [featureName]="'gallery-next-page'">
                            <mat-icon>chevron_right</mat-icon>
                        </por-base-button>
                    </div>
                </div>
            </ng-template>
        </mat-tab>
    </mat-tab-group>

    <!-- Preparing-crop spinner: shown while we fetch the image bytes through the proxy -->
    <div class="uploading-container" *ngIf="state() === 'cropping' && preparingCrop()">
        <div class="spinner"></div>
        <p>{{ 'aiImagePreparingImage' | translate }}</p>
    </div>

    <!-- Cropper: outside tab group, gets full mat-dialog-content space -->
    <div class="cropper-container" *ngIf="state() === 'cropping' && selectedImageFile() as imageFile">
        <image-cropper
            [imageFile]="imageFile"
            [maintainAspectRatio]="true"
            [aspectRatio]="cropAspectRatio"
            [resizeToWidth]="cropTargetWidth"
            [resizeToHeight]="cropTargetHeight"
            [format]="cropFormat"
            output="blob"
            [allowMoveImage]="true"
            [transform]="transform()"
            (transformChange)="onTransformChange($event)"
            [backgroundColor]="backgroundColor()"
            (imageCropped)="onImageCropped($event)"
            (imageLoaded)="onImageLoaded($event)"
            (cropperReady)="onCropperReady()"
            (loadImageFailed)="onCropperFailed()"></image-cropper>

        <!-- Zoom controls -->
        <div class="zoom-controls" *ngIf="cropperReady()">
            <span class="zoom-label">{{ 'aiImageZoomLabel' | translate }}</span>
            <span class="zoom-hint">{{ 'aiImageZoomFitAll' | translate }}</span>
            <mat-slider class="zoom-slider" [min]="minScale()" [max]="maxScale" step="0.01" [discrete]="false">
                <input matSliderThumb [value]="transform().scale ?? defaultScale()" (valueChange)="onZoomChange($event)" />
            </mat-slider>
            <span class="zoom-hint">{{ maxScale }}x</span>
            <button mat-icon-button type="button" (click)="resetZoom()" [attr.aria-label]="'aiImageZoomReset' | translate" [matTooltip]="'aiImageZoomReset' | translate">
                <mat-icon>restart_alt</mat-icon>
            </button>
        </div>

        <!-- Background controls -->
        <div class="bg-controls" *ngIf="cropperReady()">
            <span class="bg-label">{{ 'aiImageBackgroundLabel' | translate }}</span>
            <mat-radio-group [value]="backgroundColor()" (change)="onBackgroundChange($event.value)">
                <mat-radio-button value="#ffffff">{{ 'aiImageBackgroundWhite' | translate }}</mat-radio-button>
                <mat-radio-button value="#000000">{{ 'aiImageBackgroundBlack' | translate }}</mat-radio-button>
                <mat-radio-button *ngIf="showTransparentBg" value="transparent">{{ 'aiImageBackgroundTransparent' | translate }}</mat-radio-button>
            </mat-radio-group>
        </div>
    </div>

    <!-- Replace confirmation: BELOW cropper, top-to-bottom flow -->
    <div class="replace-confirmation" *ngIf="state() === 'cropping' && showReplaceConfirmation()">
        <p>{{ 'aiImageReplaceConfirmation' | translate }}</p>
        <div class="confirmation-actions">
            <por-base-button type="button" class="transparent" [disabled]="uploading()" (onclick)="cancelReplace()" [featureName]="'ai-dialog-cancel-replace'">
                <span>{{ 'Cancel' | translate }}</span>
            </por-base-button>
            <por-base-button type="button" class="btn-primary" [disabled]="uploading()" (onclick)="confirmReplace()" [featureName]="'ai-dialog-confirm-replace'">
                <div *ngIf="uploading()" class="inline-spinner"></div>
                <span>{{ 'Confirm' | translate }}</span>
            </por-base-button>
        </div>
    </div>

    <!-- Uploading indicator: visible during image upload after confirm -->
    <div class="uploading-container" *ngIf="state() === 'cropping' && uploading() && !showReplaceConfirmation()">
        <div class="spinner"></div>
        <p>{{ 'aiImageUploadingMessage' | translate }}</p>
    </div>

    <!-- Error message (visible during cropping, e.g. upload failure) -->
    <div class="error-message" *ngIf="state() === 'cropping' && errorMessage">
        <mat-icon color="warn">error</mat-icon>
        <span>{{ errorMessage | translate }}</span>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <!-- Cancel: always visible -->
    <por-base-button type="button" class="outlined" [disabled]="uploading()" (onclick)="cancel()" [featureName]="'ai-dialog-cancel'">
        <span>{{ 'Cancel' | translate }}</span>
    </por-base-button>

    <!-- Back: in cropping state -->
    <por-base-button type="button" class="outlined" [disabled]="uploading()" *ngIf="state() === 'cropping'" (onclick)="backToSource()" [featureName]="'ai-dialog-back'">
        <span>{{ 'aiImageBackToResults' | translate }}</span>
    </por-base-button>

    <!-- Generate: Generate tab, not in cropping state -->
    <por-base-button
        type="button"
        class="btn-primary"
        *ngIf="state() !== 'cropping' && selectedTabIndex() === 0"
        [disabled]="!prompt.trim() || generating() || hourlyLimitReached()"
        (onclick)="generate()"
        [featureName]="'ai-dialog-generate'">
        <div *ngIf="generating()" class="inline-spinner"></div>
        <span>{{ 'aiImageGenerateAction' | translate }}</span>
    </por-base-button>

    <!-- Use This Image: from Generate tab selection -->
    <por-base-button
        type="button"
        class="btn-primary"
        *ngIf="state() === 'selected' && selectedTabIndex() === 0"
        [disabled]="!selectedImage"
        (onclick)="useSelectedImage()"
        [featureName]="'ai-dialog-use-image'">
        <span>{{ 'aiImageUseSelectedAction' | translate }}</span>
    </por-base-button>

    <!-- Use This Image: from Gallery tab selection -->
    <por-base-button
        type="button"
        class="btn-primary"
        *ngIf="selectedTabIndex() === 1 && selectedGalleryImage() !== null && state() !== 'cropping'"
        (onclick)="useGalleryImage()"
        [featureName]="'ai-gallery-use-image'">
        <span>{{ 'aiImageUseSelectedAction' | translate }}</span>
    </por-base-button>

    <!-- Apply & Save: in cropping state, disabled when replace confirmation is showing or uploading -->
    <por-base-button
        type="button"
        class="btn-primary"
        *ngIf="state() === 'cropping'"
        [disabled]="!croppedBlob || uploading() || showReplaceConfirmation()"
        (onclick)="applyCrop()"
        [featureName]="'ai-dialog-apply-crop'">
        <span>{{ 'aiImageApplyCrop' | translate }}</span>
    </por-base-button>
</mat-dialog-actions>
